/**
 * The radio.
 *
 * Sits where the keybind list used to. That list has moved into the cockpit
 * itself — it is a card on the console you glance down at — which frees the
 * corner for the thing a corner panel is actually for: traffic you did not
 * initiate.
 *
 * ── One log, filtered ───────────────────────────────────────────────────────
 *
 * The channels are switches, not tabs. Tabs meant only one channel was ever on
 * screen, so anything said on the others arrived unseen and out of order — you
 * would tab across and find a conversation that had already happened. Now every
 * channel you have switched on shares a single log in the order things were
 * said, each line tagged with where it came from. Switching one off hides it; it
 * does not move it somewhere else.
 *
 * Transmitting still needs one destination, so that is its own control beside
 * the box — and it can only be a channel you are actually listening to, since
 * talking into a channel whose replies you never see is worse than silence.
 *
 * Local only for now. There is no comms channel on the server yet, so the
 * flight view feeds it the one thing it already knows how to say: what station
 * control is telling you about your approach. When there is a room to talk to,
 * `onSend` gets a socket behind it and nothing here changes.
 *
 * ── It folds away on a phone ────────────────────────────────────────────────
 *
 * 268 points of log and a text field is a fair trade for a corner of a desktop
 * and most of a phone held sideways — and for a great deal of the time it is
 * showing nothing that has happened recently.
 *
 * So on a touch device it is a button until there is something to read. Traffic
 * opens it by itself and holds it open for a minute past the last line, which
 * covers the case that matters: an arriving hail is not something to go looking
 * for. After that it folds back, unless it was opened by hand — a panel that
 * closed itself while somebody was typing into it would be worse than one that
 * never folded at all.
 */

import { useEffect, useRef, useState } from 'react';

import { HUD_BACKING, HUD_DIM, HUD_EDGE, HUD_ORANGE, HUD_TEXT } from './FlightHud';
import { TOUCH_UI, hudScaled } from './hudScale';

/**
 * How long a line keeps the panel open after it lands.
 *
 * Long enough to glance up at something that arrived while you were flying,
 * short enough that the corner comes back. Only consulted on touch.
 */
const RECENT = 60_000;

/** How often the clock above is re-checked. Nothing here needs a fine grain. */
const TICK = 5_000;

export type RadioChannel = 'local' | 'station' | 'faction';

export interface RadioMessage {
  id: string;
  channel: RadioChannel;
  /** Who is speaking. Station control, another pilot, or you. */
  from: string;
  /**
   * The speaker's ship, when a ship is speaking.
   *
   * A chaostree account has a username and a short display id, and they answer
   * different questions: the username is the person, the callsign is the hull.
   * Control has neither and leaves this empty.
   */
  callsign?: string;
  text: string;
  /** Yours, so it can be told apart from everyone else's at a glance. */
  own?: boolean;
}

const CHANNELS: { id: RadioChannel; label: string; colour: string }[] = [
  { id: 'local', label: 'local', colour: '#7fd4ff' },
  { id: 'station', label: 'station', colour: '#7fb46f' },
  { id: 'faction', label: 'faction', colour: '#c98bff' },
];

const COLOUR_OF = new Map(CHANNELS.map((channel) => [channel.id, channel.colour]));

export interface FlightRadioProps {
  /** The tube, when the ship's power is going. Null when it is off entirely. */
  power?: React.CSSProperties | null;
  messages: readonly RadioMessage[];
  onSend: (channel: RadioChannel, text: string) => void;
}

export function FlightRadio({ messages, onSend, power }: FlightRadioProps) {
  const [listening, setListening] = useState<Set<RadioChannel>>(
    () => new Set<RadioChannel>(['local', 'station', 'faction']),
  );
  const [sendOn, setSendOn] = useState<RadioChannel>('local');
  const [draft, setDraft] = useState('');

  /*
   * When the last line landed, and roughly what time it is now.
   *
   * A `RadioMessage` carries no timestamp — it has never needed one — so this
   * watches the count instead and notes when it moves. Which is enough: the
   * question is only ever "has anything arrived lately", and a line that arrived
   * before this panel was mounted is not something to reopen it for.
   */
  const [lastTraffic, setLastTraffic] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  /** Opened by hand, and therefore not to be closed by a timer. */
  const [opened, setOpened] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);
  const draftRef = useRef<HTMLInputElement>(null);

  const shown = messages.filter((message) => listening.has(message.channel));

  // Stick to the newest line. A log that has to be dragged back down every time
  // something arrives is a log nobody reads.
  useEffect(() => {
    const log = logRef.current;
    if (log) log.scrollTop = log.scrollHeight;
  }, [shown.length]);

  /*
   * A line that arrives, not a log that already exists.
   *
   * The panel is remounted every time the console comes back up, and comparing
   * against zero would have any past conversation count as fresh traffic — so
   * every jump would arrive to an open radio reporting a hail from two systems
   * ago. Seeded from the length at mount, so only growth counts.
   */
  const seen = useRef(messages.length);

  useEffect(() => {
    if (messages.length > seen.current) setLastTraffic(Date.now());
    seen.current = messages.length;
  }, [messages.length]);

  useEffect(() => {
    // Only where anything folds. On a desktop this clock would drive a re-render
    // every five seconds to decide a question nobody is asking.
    if (!TOUCH_UI) return;

    const id = window.setInterval(() => setNow(Date.now()), TICK);
    return () => window.clearInterval(id);
  }, []);

  const busy = now - lastTraffic < RECENT;

  // A half-typed line counts as being in use. Folding the panel away underneath
  // somebody mid-sentence would lose it, and the minute runs out exactly when a
  // slow reply is being composed.
  const folded = TOUCH_UI && !busy && !opened && draft.length === 0;

  const toggle = (channel: RadioChannel) => {
    setListening((current) => {
      const next = new Set(current);
      if (next.has(channel)) next.delete(channel);
      else next.add(channel);

      if (!next.has(sendOn)) {
        const fallback = CHANNELS.find((option) => next.has(option.id));
        if (fallback) setSendOn(fallback.id);
      }
      return next;
    });
  };

  const send = () => {
    const text = draft.trim();
    if (!text || !listening.has(sendOn)) return;
    onSend(sendOn, text);
    setDraft('');

    // Sending is the end of the errand, so the keyboard goes back to the ship.
    // Staying focused after a transmission is how you end up flying a ship that
    // is quietly typing.
    draftRef.current?.blur();
  };

  if (folded) {
    return (
      <button
        type="button"
        onClick={() => setOpened(true)}
        title="Open the radio"
        style={{
          ...power,
          ...hudScaled('top right'),
          position: 'absolute',
          top: 16,
          right: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          padding: '7px 11px',
          background: HUD_BACKING,
          border: `1px solid ${HUD_EDGE}`,
          color: HUD_DIM,
          font: 'inherit',
          fontSize: 10.5,
          letterSpacing: 1.4,
          textTransform: 'uppercase',
          pointerEvents: 'auto',
        }}
      >
        {/* Unlit, because a folded panel means nothing has been said lately —
            anything arriving unfolds it rather than lighting a pip here. */}
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            border: `1px solid ${HUD_DIM}`,
          }}
        />
        radio
      </button>
    );
  }

  return (
    <div
      /*
       * Keeps the flight view's stand-in cursor out once you click in here,
       * instead of it being stowed the moment alt is released. Typing needs
       * both hands and an open-ended amount of time; holding a modifier down
       * throughout made the radio unusable for the one thing it is for. A click
       * anywhere outside this panel hands the ship back.
       */
      data-holds-cursor
      style={{
        ...power,
        ...hudScaled('top right'),
        position: 'absolute',
        top: 16,
        right: 16,
        width: 268,
        background: HUD_BACKING,
        border: `1px solid ${HUD_EDGE}`,
        color: HUD_TEXT,
        display: 'flex',
        flexDirection: 'column',
        pointerEvents: 'auto',
      }}
    >
      <div style={{ display: 'flex', borderBottom: `1px solid ${HUD_EDGE}` }}>
        {/* Only where it folds. On a desktop there is nothing to fold it into,
            and a close button on a panel that always comes back is a button that
            lies about what it does. */}
        {TOUCH_UI && (
          <button
            type="button"
            onClick={() => {
              setOpened(false);
              // Or it would spring straight back: traffic within the last minute
              // is what holds it open, and asking for it closed has to outrank
              // that or the control does nothing.
              setLastTraffic(0);
            }}
            title="Fold the radio away"
            style={{
              padding: '5px 9px',
              background: 'transparent',
              border: 'none',
              borderRight: `1px solid ${HUD_EDGE}`,
              color: HUD_DIM,
              font: 'inherit',
              fontSize: 11,
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        )}
        {CHANNELS.map((option) => {
          const on = listening.has(option.id);
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => toggle(option.id)}
              title={`${on ? 'Stop' : 'Start'} listening on ${option.label}`}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
                padding: '5px 0',
                background: 'transparent',
                border: 'none',
                borderRight: `1px solid ${HUD_EDGE}`,
                color: on ? HUD_TEXT : HUD_DIM,
                font: 'inherit',
                fontSize: 9.5,
                letterSpacing: 1,
                textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              {/* A lit pip per channel, so which are open reads at a glance and
                  the colour matches the tag on each line below. */}
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: on ? option.colour : 'transparent',
                  border: `1px solid ${on ? option.colour : HUD_DIM}`,
                }}
              />
              {option.label}
            </button>
          );
        })}
      </div>

      {/* Fixed height, so arriving traffic never pushes the rest of the screen
          around and an empty log is the same shape as a busy one. */}
      <div
        ref={logRef}
        style={{
          height: 132,
          overflowY: 'auto',
          padding: '7px 9px',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          fontSize: 11,
          lineHeight: 1.35,
        }}
      >
        {shown.length === 0 && (
          <div style={{ color: HUD_DIM, fontSize: 10.5 }}>
            {listening.size === 0 ? 'all channels muted' : 'nothing on the air'}
          </div>
        )}
        {shown.map((message) => (
          <div key={message.id}>
            <span style={{ color: COLOUR_OF.get(message.channel), opacity: 0.75, marginRight: 4 }}>
              [{message.channel[0]}]
            </span>
            {/* The pilot, then the hull they are flying.
                A chaostree account has a username and a short display id, and
                they answer different questions — who said this, and which ship
                out there said it. Control has no callsign and shows none. */}
            <span style={{ color: message.own ? HUD_ORANGE : HUD_TEXT, marginRight: 5 }}>
              {message.from}
              {message.callsign && (
                <span style={{ color: HUD_DIM, marginLeft: 3 }}>({message.callsign})</span>
              )}
            </span>
            <span style={{ opacity: 0.85 }}>{message.text}</span>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', borderTop: `1px solid ${HUD_EDGE}` }}>
        {/* Where a transmission goes. Cycles rather than opening a menu — there
            are three channels and a menu would be more clicks than that. */}
        <button
          type="button"
          onClick={() => {
            const open = CHANNELS.filter((option) => listening.has(option.id));
            if (open.length === 0) return;
            const at = open.findIndex((option) => option.id === sendOn);
            setSendOn(open[(at + 1) % open.length]!.id);
          }}
          title="Choose which channel to transmit on"
          style={{
            width: 62,
            background: 'transparent',
            border: 'none',
            borderRight: `1px solid ${HUD_EDGE}`,
            color: COLOUR_OF.get(sendOn),
            font: 'inherit',
            fontSize: 9.5,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          {sendOn}
        </button>
        <input
          ref={draftRef}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          /*
           * Keys are stopped here rather than left to bubble: the flight loop
           * reads W, A, S and D off the window, and typing "was" into the radio
           * would otherwise fly the ship.
           *
           * ── Except Alt, which is how you get out ──────────────────────────
           *
           * Alt is the cursor key. Swallowing it meant the flight loop never
           * saw the key-*up*, so releasing Alt did not put the cursor away and
           * every other key was being eaten by a focused text field — the ship
           * stopped answering to anything and there was no way back to it
           * without reloading. That was the radio trap. Alt now passes through
           * in both directions, and letting go of it hands the controls back.
           */
          onKeyDown={(event) => {
            if (event.key === 'Alt') return;
            event.stopPropagation();

            // Out without transmitting.
            if (event.key === 'Escape') event.currentTarget.blur();
            if (event.key === 'Enter') send();
          }}
          onKeyUp={(event) => {
            if (event.key === 'Alt') return;
            event.stopPropagation();
          }}
          placeholder="transmit… (enter sends, esc exits)"
          style={{
            flex: 1,
            minWidth: 0,
            padding: '6px 9px',
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: HUD_TEXT,
            font: 'inherit',
            fontSize: 11,
          }}
        />
        <button
          type="button"
          onClick={send}
          style={{
            width: 46,
            background: 'transparent',
            border: 'none',
            borderLeft: `1px solid ${HUD_EDGE}`,
            color: HUD_ORANGE,
            font: 'inherit',
            fontSize: 9.5,
            letterSpacing: 1,
            textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          send
        </button>
      </div>
    </div>
  );
}
